/**
 * Helpdesk landing page, built like the Material Compliance start page: a title
 * with the two ways to get an answer, then one tile per area.
 *
 * The page's one yellow button is the agent. It stands beside the search because
 * they answer the same need, and the helpdesk is where someone who is stuck
 * arrives.
 *
 * Search replaces the browse view while there is a query, rather than sitting
 * beside it: at this corpus size a reader either knows what to look for or wants
 * to browse, and showing both at once made neither easy to scan.
 */

import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { URLS } from "@/utils/urls.const"
import { Mail, MessageCircle, Pencil } from "lucide-react"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router"
import type { HelpContent } from "../api/types"
import { featuredArticles } from "../content"
import { REVEAL_GROUP, REVEAL_ITEM, SWAP } from "../motion"
import { HELP_MANAGE } from "../routes"
import { searchArticles } from "../search"
import { ArticleList, ArticleRow, CategoryTile } from "./HelpdeskCards"
import { useAskAgent } from "./HelpdeskEmptyState"
import { HelpdeskSearchField } from "./HelpdeskSearchField"

const SEARCH_MISS_DELAY_MS = 800
const MIN_LOGGED_QUERY = 3

/** The module idiom for a small uppercase label, as on the tiles below it. */
const LABEL_CLASS =
  "font-mono text-[10px] tracking-[0.18em] text-grey5 uppercase"

/**
 * The fastest answer on the page, so it is the one thing here that keeps moving:
 * a slow glow behind it and a sheen across it. The ring runs once on arrival, and
 * again when a search settles on nothing, which is when someone needs it most.
 */
function AskAgentButton({ searchFailed }: { searchFailed: boolean }) {
  const { translate } = useTranslation()
  const { canAskAgent, openAgent } = useAskAgent()
  if (!canAskAgent) return null
  return (
    // The keyboard outline in white: the app's yellow ring vanishes on this button.
    <Button
      onClick={openAgent}
      className="group relative isolate transition-[transform,background-color] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white focus-visible:outline-solid active:translate-y-px"
    >
      {/* Keyed on its own, so the ring restarts without restarting the glow. */}
      <span
        key={searchFailed ? "search-failed" : "arrival"}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 animate-focus-pulse rounded-md motion-reduce:animate-none"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-1 -z-10 animate-agent-glow rounded-lg bg-primary blur-md motion-reduce:animate-none motion-reduce:opacity-25"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
      >
        <span className="absolute inset-y-0 -left-1/2 w-1/2 animate-agent-sheen bg-gradient-to-r from-transparent via-white/70 to-transparent motion-reduce:hidden" />
      </span>
      <MessageCircle
        aria-hidden="true"
        className="relative transition-transform duration-150 motion-safe:group-hover:scale-110 motion-safe:group-hover:-rotate-6"
      />
      <span className="relative">{translate("HELPDESK/EMPTY_ASK_AGENT")}</span>
    </Button>
  )
}

/** The way out at the end of the page, and when a search found nothing. */
function ContactStrip({ message }: { message: string }) {
  const { translate } = useTranslation()
  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-dashed border-grey4 px-5 py-4">
      <Mail
        size={24}
        strokeWidth={1.25}
        aria-hidden="true"
        className="shrink-0 text-grey5"
      />
      <p className="min-w-0 flex-1 text-sm text-white">{message}</p>
      <Button asChild variant="outline">
        <a href={URLS.INFO_BOX.HELP}>{translate("HELPDESK/CONTACT_US")}</a>
      </Button>
    </div>
  )
}

export function HelpdeskOverview({ content }: { content: HelpContent }) {
  const { translate } = useTranslation()
  const canEdit = useFeatureFlag("HELPDESK_EDIT")
  const [query, setQuery] = useState("")

  const hits = useMemo(
    () => (query.trim() ? searchArticles(content, query) : []),
    [content, query],
  )
  const featured = useMemo(() => featuredArticles(content), [content])
  const isSearching = query.trim().length > 0

  // A query that found nothing is the clearest signal of a page we have not
  // written. Logged once the typing settles, so one search is one event.
  const settledQuery = useDebouncedValue(query.trim(), SEARCH_MISS_DELAY_MS)
  useEffect(() => {
    if (settledQuery.length < MIN_LOGGED_QUERY || settledQuery !== query.trim())
      return
    if (hits.length > 0) return
    trackEvent(AnalyticsEvent.HELP_SEARCH_MISSED, {
      query: settledQuery,
      lang: content.lang,
    })
  }, [settledQuery, query, hits.length, content.lang])
  const searchFailed =
    settledQuery.length > 0 &&
    settledQuery === query.trim() &&
    hits.length === 0

  return (
    // A container, so the columns answer to the room the docked chat leaves.
    <div className="@container relative h-full min-w-0 overflow-x-hidden overflow-y-auto bg-grey3">
      <div className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[720px] animate-glow-drift rounded-full bg-yellow1/[0.07] blur-3xl motion-reduce:animate-none" />

      <m.div
        variants={REVEAL_GROUP}
        initial="hidden"
        animate="shown"
        className="relative mx-auto max-w-[1460px] px-4 pt-8 pb-14 md:px-9"
      >
        {/* Stays put through a search: only what is below it changes, so the
            field someone is typing in never moves under them. */}
        <div
          className={cn(
            "grid items-center gap-8",
            featured.length > 0 &&
              "@5xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] @5xl:gap-12",
          )}
        >
          <m.div variants={REVEAL_ITEM}>
            <div className="flex items-start justify-between gap-4">
              <Typography as="h1" variant="titleL">
                {translate("HELPDESK/TITLE")}
              </Typography>
              {canEdit ? (
                <Button asChild size="sm" variant="outline">
                  <Link to={HELP_MANAGE}>
                    <Pencil aria-hidden="true" />
                    {translate("HELPDESK/MANAGE_TITLE")}
                  </Link>
                </Button>
              ) : null}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-grey5">
              {translate("HELPDESK/SUBTITLE")}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <HelpdeskSearchField
                value={query}
                onChange={setQuery}
                placeholder={translate("HELPDESK/SEARCH_PLACEHOLDER")}
                className="min-w-[min(15rem,100%)]"
              />
              <AskAgentButton searchFailed={searchFailed} />
            </div>
          </m.div>

          {featured.length > 0 ? (
            <m.section
              variants={REVEAL_ITEM}
              className="rounded-xl border border-grey4 bg-grey2 px-3 pt-4 pb-1"
            >
              <h2 className={cn(LABEL_CLASS, "pb-2")}>
                {translate("HELPDESK/FIRST_STEPS")}
              </h2>
              <ArticleList>
                {featured.map(({ category, article }) => (
                  <ArticleRow
                    key={article.id}
                    article={article}
                    categoryId={category.id}
                    visualKey={category.visualKey}
                    compact
                  />
                ))}
              </ArticleList>
            </m.section>
          ) : null}
        </div>

        <AnimatePresence mode="wait">
          {isSearching ? (
            <m.section
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={SWAP}
              className="mt-8"
            >
              {hits.length > 0 ? (
                <>
                  <h2 className={cn(LABEL_CLASS, "pb-3")}>
                    {translate("HELPDESK/SEARCH_RESULTS", {
                      count: hits.length,
                    })}
                  </h2>
                  <ArticleList className="rounded-xl border border-grey4 bg-grey2 px-3">
                    {hits.map(({ category, article }) => (
                      <ArticleRow
                        key={article.id}
                        article={article}
                        categoryId={category.id}
                        visualKey={category.visualKey}
                        categoryTitle={category.title}
                      />
                    ))}
                  </ArticleList>
                </>
              ) : (
                <ContactStrip
                  message={translate("HELPDESK/SEARCH_NO_RESULTS")}
                />
              )}
            </m.section>
          ) : (
            <m.div
              key="browse"
              variants={REVEAL_GROUP}
              exit={{ opacity: 0, transition: SWAP }}
            >
              {/* Fits its tiles, since how many areas someone has depends on their
                  flags, and a fourth should not sit alone on a row of its own. */}
              <section
                aria-label={translate("HELPDESK/AREAS")}
                className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(min(15rem,100%),1fr))] gap-[18px]"
              >
                {content.categories.map((category) => (
                  <m.div key={category.id} variants={REVEAL_ITEM}>
                    <CategoryTile category={category} />
                  </m.div>
                ))}
              </section>
              <m.div variants={REVEAL_ITEM}>
                <ContactStrip message={translate("HELPDESK/STILL_STUCK")} />
              </m.div>
            </m.div>
          )}
        </AnimatePresence>
      </m.div>
    </div>
  )
}
